// src/component/page/album/Craftingnft.jsx
import {
  Alert,
  Box,
  Button,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Snackbar,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { rutas } from "../../../rutas/rutas";
import { getCookie } from "../../../utils/getCookie";
import { setInformacion } from "../../../storage/slices/usuarios";

// --- Local helpers for user counting (no API)
const RAREZAS = ["C", "R", "Z", "E", "A", "L"];
const baseRarezas = () => RAREZAS.reduce((m, r) => ((m[r] = 0), m), {});

/**
 * Burn NFTs
 */

// Bone table (must match backend)
const BONE_VALUES = {
  general: { C: 100, R: 300, Z: 700, E: 1200, A: 1500, L: 2500 },
  especial: { C: 200, R: 600, Z: 1000, E: 2000, A: 3000, L: 5000 },
};

// NFT symbol (must match backend)
const NFT_SYMBOL =
  process.env.REACT_APP_NFT_SYMBOL ||
  (typeof window !== "undefined" && window.REACT_APP_NFT_SYMBOL) ||
  "NFTHUESO";

// ---- Pending/resume helpers (localStorage, with cool-down)
const PENDING_KEY = "pendingBurn";
const RESUME_COOLDOWN_KEY = "he:burn-last-resume";
const MAX_PENDING_AGE_MIN = 15; // expires after 15min
const RESUME_COOLDOWN_MIN = 2; // minimum 2min between manual retries

// Calculates Bone from a rarity bucket and whether it's "general" or "especial"
const bonesFrom = (bucket, tipo) =>
  (bucket?.C || 0) * BONE_VALUES[tipo].C +
  (bucket?.R || 0) * BONE_VALUES[tipo].R +
  (bucket?.Z || 0) * BONE_VALUES[tipo].Z +
  (bucket?.E || 0) * BONE_VALUES[tipo].E +
  (bucket?.A || 0) * BONE_VALUES[tipo].A +
  (bucket?.L || 0) * BONE_VALUES[tipo].L;

const savePending = (obj) => {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(obj));
  } catch {}
};
const readPending = () => {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
  } catch {
    return null;
  }
};
const clearPending = () => {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {}
};
const markResumeNow = () => {
  try {
    localStorage.setItem(RESUME_COOLDOWN_KEY, String(Date.now()));
  } catch {}
};
const canResumeNow = (mins = RESUME_COOLDOWN_MIN) => {
  try {
    const last = Number(localStorage.getItem(RESUME_COOLDOWN_KEY) || 0);
    return Date.now() - last > mins * 60 * 1000;
  } catch {
    return true;
  }
};

/* =========================================================
   HELPER: resolve NFT lists wherever they are
   ========================================================= */
const getListsFromInfo = (info) => {
  const gen =
    Array.isArray(info?.nft)
      ? info.nft
      : Array.isArray(info?.informacion?.nft)
      ? info.informacion.nft
      : Array.isArray(info?.generalNft)
      ? info.generalNft
      : [];

  const esp =
    Array.isArray(info?.nftEspecial)
      ? info.nftEspecial
      : Array.isArray(info?.informacion?.nftEspecial)
      ? info.informacion.nftEspecial
      : Array.isArray(info?.especialNft)
      ? info.especialNft
      : [];

  return { gen, esp };
};

export const Craftingnft = () => {
  const { especiales = [], generalNft = [] } = useSelector((s) => s.userSlice);
  const { informacion } = useSelector((s) => s.userSlice);

  const [barajitas, setBarajita] = useState([]);
  const [seleccion, setSeleccion] = useState([]);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackErr, setSnackErr] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Processing state / overlay
  const [processing, setProcessing] = useState(false);
  const [processingMsg, setProcessingMsg] = useState("");

  // Resume banner (no auto-resume)
  const [resumeBanner, setResumeBanner] = useState(null); // { txid, nfts, totalBone }
  const resumingRef = useRef(false);

  // Tabs (individual | global | burned)
  const [tab, setTab] = useState("individual");

  // GLOBAL totals (server)
  const [globalTotals, setGlobalTotals] = useState({
    general: baseRarezas(),
    especial: baseRarezas(),
    totals: baseRarezas(),
  });
  const [globalLoading, setGlobalLoading] = useState(false);

  const dispatch = useDispatch();
  const handleSnackClose = () => setSnackOpen(false);

  // ide -> url fallback (static cards catalog)
  const catalogoUrl = useMemo(() => {
    const map = new Map();
    [...especiales, ...generalNft].forEach((c) => {
      if (c?.ide && c?.url) map.set(c.ide, c.url);
    });
    return map;
  }, [especiales, generalNft]);

  // Load inventory with on-chain IDs (USING THE HELPER)
  const loadInventario = () => {
    try {
      const { gen: fromGen, esp: fromEsp } = getListsFromInfo(informacion);

      const merged = [...fromGen, ...fromEsp].map((i) => {
        const ids = Array.isArray(i?.nft) ? i.nft : [];
        return {
          id: i?.id ?? String(i?.ide ?? ""),
          ide: i?.ide,
          rareza: i?.rareza,
          url: i?.url || catalogoUrl.get(i?.ide) || "",
          familia: i?.familia,
          cantidad: ids.length,
          nft: ids,
        };
      });

      setBarajita(merged);
    } catch (e) {
      console.error("[BURN] loadInventario() error", e);
    }
  };

  useEffect(() => {
    loadInventario();
  }, [catalogoUrl, informacion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ TOTAL count for the user (does not discount current selection)
  const userTotals = useMemo(() => {
    const acc = {
      general: baseRarezas(),
      especial: baseRarezas(),
      totals: baseRarezas(),
    };
    const { gen: genList, esp: espList } = getListsFromInfo(informacion);

    for (const c of genList) {
      const r = c?.rareza || "C";
      const qty =
        Array.isArray(c?.nft) ? c.nft.length : Number(c?.cantidad || 0) || 0;
      if (acc.general[r] != null) acc.general[r] += qty;
    }
    for (const c of espList) {
      const r = c?.rareza || "C";
      const qty =
        Array.isArray(c?.nft) ? c.nft.length : Number(c?.cantidad || 0) || 0;
      if (acc.especial[r] != null) acc.especial[r] += qty;
    }

    for (const r of RAREZAS) {
      acc.totals[r] = (acc.general[r] || 0) + (acc.especial[r] || 0);
    }
    return acc;
  }, [informacion]);

  // === GLOBAL endpoint URL ===
  const baseUrl = rutas?.globalNftCounts || null;

  // Normalize backend payload
  const normalizeGlobal = (payload) => {
    const g = { ...baseRarezas(), ...(payload?.general || {}) };
    const e = { ...baseRarezas(), ...(payload?.especial || {}) };
    const t = { ...baseRarezas() };
    for (const r of RAREZAS) t[r] = (g[r] || 0) + (e[r] || 0);
    return { general: g, especial: e, totals: t };
  };

  // ✅ Reusable: reload net GLOBAL totals
  const reloadGlobalTotals = async () => {
    if (!baseUrl) return;
    try {
      setGlobalLoading(true);
      const url = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}prefer=nft&net=1`;
      const res = await axios.get(url, {
        headers: { "Content-Type": "application/json" },
      });
      setGlobalTotals(normalizeGlobal(res?.data || {}));
    } catch (_e) {
      setGlobalTotals({
        general: baseRarezas(),
        especial: baseRarezas(),
        totals: baseRarezas(),
      });
    } finally {
      setGlobalLoading(false);
    }
  };

  // 🚀 Initial fetch for GLOBAL totals
  useEffect(() => {
    reloadGlobalTotals();
  }, [baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Block closing/reloading if processing
  useEffect(() => {
    const handler = (e) => {
      if (!processing) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [processing]);

  // On mount: show banner if there is a valid pending burn
  useEffect(() => {
    const pending = readPending();
    if (!pending) return;

    const tooOld =
      Date.now() - Number(pending?.startedAt || pending?.ts || 0) >
      MAX_PENDING_AGE_MIN * 60 * 1000;

    if (
      tooOld ||
      !pending?.proofTx ||
      !Array.isArray(pending?.nfts) ||
      !pending.nfts.length
    ) {
      clearPending();
      return;
    }

    setResumeBanner({
      txid: pending.proofTx,
      nfts: pending.nfts,
      totalBone: pending.totalBone || 0,
    });
  }, []);

  // Add to selection (do not exceed stock)
  const addBarajita = (card) => {
    const alreadySelected = seleccion.filter((c) => c?.id === card?.id).length;
    const stock = Array.isArray(card?.nft)
      ? card.nft.length
      : Number(card?.cantidad || 0);
    if (alreadySelected < stock) {
      setSeleccion((s) => [...s, { ...card, ideFront: uuidv4() }]);
    }
  };
  const deleteBarajita = (card) =>
    setSeleccion((s) => s.filter((c) => c.ideFront !== card.ideFront));

  // Estimated Bone per card
  const bonePer = (c) => {
    const setKey = c?.id?.[0] === "E" ? "especial" : "general";
    const r = c?.rareza || "C";
    return BONE_VALUES[setKey]?.[r] || 0;
  };

  const totalBone = useMemo(
    () => seleccion.reduce((acc, c) => acc + bonePer(c), 0),
    [seleccion]
  );

  // Group selection by id
  const seleccionAgrupada = useMemo(() => {
    const map = new Map();
    for (const c of seleccion) {
      const prev =
        map.get(c.id) || { id: c.id, ide: c.ide, rareza: c.rareza, cantidad: 0 };
      prev.cantidad += 1;
      map.set(c.id, prev);
    }
    return Array.from(map.values());
  }, [seleccion]);

  // Build payload with exact instanceIds
  const buildNftsPayload = () =>
    seleccionAgrupada.map((g) => {
      const src = barajitas.find((x) => x.id === g.id);
      const ids = Array.isArray(src?.nft)
        ? src.nft.slice(0, g.cantidad).map(String)
        : [];
      return { ...g, instanceIds: ids };
    });

  // Sign with Keychain: nft.burn
  const signBurnWithKeychain = async (usuario, nfts) => {
    if (!window.hive_keychain) throw new Error("Hive Keychain is not installed.");
    const ids = nfts.flatMap((n) =>
      Array.isArray(n?.instanceIds) ? n.instanceIds : []
    );
    if (!ids.length) throw new Error("There are no instances to burn (instanceIds empty).");

    const jsonForHE = {
      contractName: "nft",
      contractAction: "burn",
      contractPayload: { nfts: [{ symbol: NFT_SYMBOL, ids: ids.map(String) }] },
    };

    return new Promise((resolve, reject) => {
      try {
        window.hive_keychain.requestCustomJson(
          String(usuario).toLowerCase(),
          "ssc-mainnet-hive",
          "Active",
          JSON.stringify(jsonForHE),
          "Burn NFTs (HE)",
          (res) => {
            if (res?.success) {
              const txid =
                res?.result?.id || res?.result || res?.txid || res?.id || "";
              resolve({ txid: String(txid) });
            } else {
              reject(
                new Error(res?.message || res?.error || "Signature canceled or failed.")
              );
            }
          }
        );
      } catch (e) {
        reject(e);
      }
    });
  };

  // POST to backend (reusable)
  const postToBackend = async ({ nfts, proofTx, totalBone }) => {
    const token = getCookie("token");
    setProcessing(true);
    setProcessingMsg("Confirming burn on Hive-Engine and crediting Hueso…");
    return axios.post(
      rutas.burnNft,
      { nfts, bonePreview: totalBone, proofTx },
      { headers: { "Content-Type": "application/json", token }, timeout: 30000 }
    );
  };

  // Retry pending burn (from banner)
  const reintentarPendiente = async () => {
    if (resumingRef.current) return;
    const pending = readPending();
    if (!pending) {
      setResumeBanner(null);
      return;
    }

    if (!canResumeNow()) {
      setSnackErr(true);
      setSnackMsg("Please wait a moment before retrying.");
      setSnackOpen(true);
      return;
    }
    markResumeNow();

    try {
      setProcessing(true);
      setProcessingMsg("Resuming pending burn…");
      const res = await postToBackend({
        nfts: pending.nfts,
        proofTx: pending.proofTx,
        totalBone: pending.totalBone || 0,
      });
      clearPending();
      setResumeBanner(null);
      setSnackErr(false);
      setSnackMsg("Burn resumed successfully.");
      setSnackOpen(true);
      if (res?.data?.resultado || res?.data?.usuario) {
        dispatch(
          setInformacion({
            ...(res.data.resultado || res.data.usuario || informacion),
          })
        );
      }
      await reloadGlobalTotals(); // 🔄 refresh GLOBAL net panel
      loadInventario();

      // 🔄 Full reload (optional: small delay to close snack/overlay)
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      setSnackErr(true);
      setSnackMsg(
        e?.response?.data?.error || e?.message || "Could not resume now."
      );
      setSnackOpen(true);
    } finally {
      setProcessing(false);
      setProcessingMsg("");
      resumingRef.current = false;
    }
  };

  const descartarPendiente = () => {
    clearPending();
    setResumeBanner(null);
    setSnackErr(false);
    setSnackMsg("The pending burn was discarded. You can try again.");
    setSnackOpen(true);
  };

  // Confirmation → sign → backend
  const abrirConfirmacion = () => {
    if (!seleccion.length) return;
    setConfirmOpen(true);
  };

  const confirmarYQuemar = async () => {
    setConfirmOpen(false);

    try {
      const nfts = buildNftsPayload();

      // Resolve account
      const usuario =
        informacion?.nombre ||
        informacion?.usuario ||
        informacion?.Nombre ||
        informacion?.wallet ||
        "";
      if (!usuario) throw new Error("The player's account could not be resolved.");

      // 1) Signature
      setProcessing(true);
      setProcessingMsg("Signing burn in Keychain…");
      const signRes = await signBurnWithKeychain(usuario, nfts);
      const txid = signRes?.txid;

      // Save pending
      savePending({ proofTx: txid, nfts, totalBone, startedAt: Date.now() });

      // 2) Backend
      const res = await postToBackend({ nfts, proofTx: txid, totalBone });

      // Success
      clearPending();
      setSnackErr(false);
      setSnackMsg(`You burned ${seleccion.length} NFT(s)! +${totalBone} Hueso`);
      setSnackOpen(true);
      setSeleccion([]);
      if (res?.data?.resultado || res?.data?.usuario) {
        dispatch(
          setInformacion({
            ...(res.data.resultado || res.data.usuario || informacion),
          })
        );
      }
      await reloadGlobalTotals(); // 🔄 refresh GLOBAL net panel
      loadInventario();

      // 🔄 Full reload (optional: small delay to close snack/overlay)
      setTimeout(() => window.location.reload(), 400);
    } catch (err) {
      if (err?.code === "ECONNABORTED") {
        setSnackErr(true);
        setSnackMsg(
          "The confirmation took longer than expected. You can retry later."
        );
        setSnackOpen(true);
        const p = readPending();
        if (p)
          setResumeBanner({
            txid: p.proofTx,
            nfts: p.nfts,
            totalBone: p.totalBone || 0,
          });
      } else {
        setSnackErr(true);
        setSnackMsg(
          err?.response?.data?.error ||
            err?.message ||
            "The burn could not be completed."
        );
        setSnackOpen(true);
      }
    } finally {
      setProcessing(false);
      setProcessingMsg("");
    }
  };

  // === BURNED (from backend) ===
  const burnedTotals = useMemo(() => {
    const b = informacion?.burned || {};
    const g = { ...baseRarezas(), ...(b.general || {}) };
    const e = { ...baseRarezas(), ...(b.especial || {}) };

    const t = b.totals
      ? { ...baseRarezas(), ...b.totals } // use as is
      : RAREZAS.reduce(
          (acc, r) => ((acc[r] = (g[r] || 0) + (e[r] || 0)), acc),
          baseRarezas()
        );

    return { general: g, especial: e, totals: t };
  }, [informacion]);

  // UI helper to render a row of chips by rarity
  const ChipsRow = ({ data }) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
      {RAREZAS.map((r) => (
        <Box
          key={r}
          sx={{
            px: 0.7,
            py: 0.3,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,.08)",
            border: "1px solid rgba(255,255,255,.14)",
            color: "#fff",
            fontSize: ".8rem",
            lineHeight: 1,
          }}
        >
          {r}: {data?.[r] || 0}
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Box sx={{ height: "100%", width: "100%", display: "flex", pb: "96px" }}>
        {/* Left: summary */}
        <Box
          sx={{ height: "100%", width: "32%", background: "rgba(0,0,0,0.3)", mr: 1 }}
        >
          <Box sx={{ height: "100%", width: "100%" }}>
            <Typography
              sx={{
                color: "white",
                fontSize: "1.3rem",
                textAlign: "center",
                textShadow: "2px 2px 3px gold",
              }}
            >
              {seleccion.length} NFT(s) selected
            </Typography>
            <Typography
              sx={{ color: "#9be7ff", fontSize: "1rem", textAlign: "center", mt: 0.5 }}
            >
              You will receive approximately <b>{totalBone}</b> Hueso
            </Typography>
            <Typography
              sx={{
                color: "#ffd54f",
                fontSize: ".9rem",
                textAlign: "center",
                px: 1,
                mt: 1,
              }}
            >
              When you burn, your NFTs are permanently removed and the
              corresponding Hueso will be credited.
            </Typography>

            {/* Tabs INDIVIDUAL / GLOBAL / BURNED */}
            <Box
              sx={{
                mt: 1.25,
                mx: 1,
                p: 1.1,
                borderRadius: 1.5,
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "nowrap",
                  alignItems: "center",
                  borderBottom: "1px solid rgba(255,255,255,.1)",
                  mb: 1,
                }}
              >
                <Button
                  onClick={() => setTab("individual")}
                  size="small"
                  sx={{
                    color: tab === "individual" ? "#fff" : "#cfd8dc",
                    borderBottom:
                      tab === "individual" ? "2px solid #fff" : "2px solid transparent",
                    borderRadius: 0,
                    textTransform: "none",
                    minWidth: 0,
                    px: 1.2,
                    fontSize: ".9rem",
                  }}
                >
                  🧑‍🚀 INDIVIDUAL
                </Button>
                <Button
                  onClick={() => setTab("global")}
                  size="small"
                  sx={{
                    color: tab === "global" ? "#fff" : "#cfd8dc",
                    borderBottom:
                      tab === "global" ? "2px solid #fff" : "2px solid transparent",
                    borderRadius: 0,
                    textTransform: "none",
                    minWidth: 0,
                    px: 1.2,
                    fontSize: ".9rem",
                  }}
                >
                  🌐 GLOBAL
                </Button>
                <Button
                  onClick={() => setTab("quemados")}
                  size="small"
                  sx={{
                    color: tab === "quemados" ? "#fff" : "#cfd8dc",
                    borderBottom:
                      tab === "quemados" ? "2px solid #fff" : "2px solid transparent",
                    borderRadius: 0,
                    textTransform: "none",
                    minWidth: 0,
                    px: 1.2,
                    fontSize: ".9rem",
                  }}
                >
                  🔥 BURNED
                </Button>
              </Box>

              {tab === "individual" ? (
                <>
                  <Typography
                    sx={{ color: "#9be7ff", fontSize: ".95rem", fontWeight: 600, mb: 0.6 }}
                  >
                    Your total NFTs by rarity
                  </Typography>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const g = bonesFrom(userTotals.general, "general");
                      const e = bonesFrom(userTotals.especial, "especial");
                      const total = g + e;
                      window.alert(
                        `Estimated Hueso (INDIVIDUAL)\n\n` +
                          `General: ${g.toLocaleString()}\n` +
                          `Special: ${e.toLocaleString()}\n` +
                          `-------------------------\n` +
                          `TOTAL: ${total.toLocaleString()}`
                      );
                    }}
                  >
                    💰 Calculate Hueso
                  </Button>

                  <Typography sx={{ color: "#fff", fontSize: ".85rem", mb: 0.35 }}>
                    G — General
                  </Typography>
                  <ChipsRow data={userTotals.general} />

                  <Typography sx={{ color: "#fff", fontSize: ".85rem", mb: 0.35 }}>
                    E — Special
                  </Typography>
                  <ChipsRow data={userTotals.especial} />

                  <Typography
                    sx={{ color: "#9be7ff", fontSize: ".85rem", mb: 0.35, fontWeight: 600 }}
                  >
                    Total (G+E)
                  </Typography>
                  <ChipsRow data={userTotals.totals} />
                </>
              ) : tab === "global" ? (
                <>
                  <Typography
                    sx={{ color: "#9be7ff", fontSize: ".95rem", fontWeight: 600, mb: 0.6 }}
                  >
                    Global NFTs (server)
                  </Typography>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const g = bonesFrom(globalTotals.general, "general");
                      const e = bonesFrom(globalTotals.especial, "especial");
                      const total = g + e;
                      window.alert(
                        `Estimated Hueso (GLOBAL)\n\n` +
                          `General: ${g.toLocaleString()}\n` +
                          `Special: ${e.toLocaleString()}\n` +
                          `-------------------------\n` +
                          `TOTAL: ${total.toLocaleString()}`
                      );
                    }}
                  >
                    💰 Calculate Hueso
                  </Button>

                  <Typography sx={{ color: "#fff", fontSize: ".85rem", mb: 0.35 }}>
                    G — General
                  </Typography>
                  {globalLoading ? (
                    <Typography sx={{ color: "#90caf9", fontSize: ".8rem", mb: 0.75 }}>
                      Loading…
                    </Typography>
                  ) : (
                    <ChipsRow data={globalTotals.general} />
                  )}

                  <Typography sx={{ color: "#fff", fontSize: ".85rem", mb: 0.35 }}>
                    E — Special
                  </Typography>
                  {globalLoading ? (
                    <Typography sx={{ color: "#90caf9", fontSize: ".8rem", mb: 0.75 }}>
                      Loading…
                    </Typography>
                  ) : (
                    <ChipsRow data={globalTotals.especial} />
                  )}

                  <Typography
                    sx={{ color: "#9be7ff", fontSize: ".85rem", mb: 0.35, fontWeight: 600 }}
                  >
                    Total (G+E)
                  </Typography>
                  {globalLoading ? (
                    <Typography sx={{ color: "#90caf9", fontSize: ".8rem" }}>
                      Loading…
                    </Typography>
                  ) : (
                    <ChipsRow data={globalTotals.totals} />
                  )}
                </>
              ) : (
                <>
                  <Typography
                    sx={{ color: "#9be7ff", fontSize: ".95rem", fontWeight: 600, mb: 0.6 }}
                  >
                    Burned NFTs (cumulative)
                  </Typography>

                  <Typography sx={{ color: "#fff", fontSize: ".85rem", mb: 0.35 }}>
                    G — General (burned)
                  </Typography>
                  <ChipsRow data={burnedTotals.general} />

                  <Typography sx={{ color: "#fff", fontSize: ".85rem", mb: 0.35 }}>
                    E — Special (burned)
                  </Typography>
                  <ChipsRow data={burnedTotals.especial} />

                  <Typography
                    sx={{ color: "#9be7ff", fontSize: ".85rem", mb: 0.35, fontWeight: 600 }}
                  >
                    Total (G+E) burned
                  </Typography>
                  <ChipsRow data={burnedTotals.totals} />
                </>
              )}
            </Box>

            {/* Selected thumbnails (smaller) */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                height: "78%",
                justifyContent: "center",
                mt: 1,
                overflowY: "auto",
              }}
            >
              {seleccion.map((card) => (
                <Box sx={{ position: "relative", width: "66px" }} key={card.ideFront}>
                  <Box sx={{ position: "absolute", color: "white", fontSize: ".7rem" }}>
                    {card?.id[0] === "E" ? "E" : "G"} • +{bonePer(card)}
                  </Box>
                  <CardMedia
                    onClick={() => deleteBarajita(card)}
                    sx={{ width: "60px", cursor: "pointer" }}
                    component="img"
                    image={card?.url}
                    alt={card?.id}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Right: inventory */}
        <Box
          sx={{
            background: "rgba(0,0,0,0.3)",
            height: "100%",
            width: "68%",
            display: "flex",
            flexWrap: "wrap",
            overflow: "auto",
            alignContent: "flex-start",
          }}
        >
          {barajitas.map((card, idx) => (
            <Box sx={{ position: "relative", width: "84px", m: "3px" }} key={`${card.id}-${idx}`}>
              <Box sx={{ position: "absolute", color: "white", fontSize: ".75rem" }}>
                {card?.id[0] === "E" ? "E" : "G"} • {card?.cantidad}
              </Box>
              <CardMedia
                onClick={() => addBarajita(card)}
                sx={{ width: "80px", cursor: "pointer" }}
                component="img"
                image={card?.url}
                alt={card?.id}
              />
            </Box>
          ))}
        </Box>

        {/* Fixed bottom button */}
        <Box
          sx={{
            position: "fixed",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 24,
            width: { xs: "90%", md: "60%" },
            zIndex: 1000,
          }}
        >
          <Button
            onClick={abrirConfirmacion}
            fullWidth
            color="error"
            variant="contained"
            size="large"
            disabled={!seleccion.length || processing}
          >
            🔥 Burn NFTs — +{totalBone} Hueso
          </Button>
        </Box>
      </Box>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm burn</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You are about to burn <b>{seleccion.length}</b> NFT(s).
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            This action is <b>irreversible</b>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmarYQuemar}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Processing overlay */}
      {processing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            color: "#fff",
            textAlign: "center",
            p: 3,
          }}
        >
          <Box sx={{ display: "grid", gap: 2, placeItems: "center", maxWidth: 560 }}>
            <CircularProgress />
            <Typography variant="h6">Processing…</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {processingMsg ||
                "Confirming burn on Hive-Engine. Do not close or reload this window."}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              If this screen takes too long, you will be able to resume manually later.
            </Typography>
          </Box>
        </Box>
      )}

      {/* “Resume pending burn” banner */}
      {resumeBanner && !processing && (
        <Box
          sx={{
            position: "fixed",
            left: 16,
            right: 16,
            bottom: 88,
            background: "#263238",
            color: "#fff",
            borderRadius: 2,
            p: 2,
            zIndex: 1500,
            boxShadow: "0 8px 24px rgba(0,0,0,.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="body2">
            You have a pending burn to confirm. Do you want to retry it now?
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={descartarPendiente}>
              Discard
            </Button>
            <Button variant="contained" color="error" onClick={reintentarPendiente}>
              Retry
            </Button>
          </Box>
        </Box>
      )}

      {/* Snackbar */}
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose}>
          {snackErr ? (
            <Alert onClose={handleSnackClose} severity="error" variant="filled" sx={{ width: "100%" }}>
              {snackMsg || "Error burning NFTs"}
            </Alert>
          ) : (
            <Alert onClose={handleSnackClose} severity="success" variant="filled" sx={{ width: "100%" }}>
              {snackMsg || "Operation successful"}
            </Alert>
          )}
        </Snackbar>
      </Stack>
    </>
  );
};
